import React, { memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import { ScommesseEsitoTipoBox } from 'components/scommesseEsitoTipoBox';
import { ScommesseTableQuotaSelect } from 'components/scommesseTipo/components/scommesseTableQuotaSelect';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import {
  useInfoAggiuntivaBilanciata,
  useInfoAggiuntivaIndex,
  useInfoAggiuntivaMeta,
  useInfoTipo,
  useIsAvvenimento,
} from 'features/sport/hooks';
import useWindowSize from 'hooks/useWindowSize';
import { useTypedSelector } from 'lib/centralStore';
import { isTruthy } from 'utility/functions';
import { ScommesseLeafItem } from 'components/scommesseTipo/components/cells';
import { ScommesseTipoProps } from 'components/scommesseTipo/types';
import styles from './ScommesseTipoEsitiUnderThreeWithInfoAgg.module.scss';
import { Skeleton } from 'components/scommesseTipo/scommesseTipoEsitiUnderThreeWithInfoAgg/Skeleton';
import { useIsAvvenimento as useIsAvvenimentoVirtual } from 'features/virtual/hooks';
import { KeyManagerSport } from 'features/sport';

export const Scommessa = memo(
  function Scommessa(props: ScommesseTipoProps) {
    const { scommessaKey, showOnlyBilanciata, gtagAction } = props;

    const { isMobile } = useWindowSize();
    const isAvvenimento = useIsAvvenimento();
    const isAvvenimentoVirtual = useIsAvvenimentoVirtual();
    const isEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);

    const infoAggMeta = useInfoAggiuntivaMeta(scommessaKey);
    const { sizeInfoAggiuntive } = infoAggMeta ?? {};

    const { tipoScommessaId } = new KeyManagerSport(scommessaKey);
    const { numeroEsiti } = useInfoTipo(tipoScommessaId) ?? {};

    const { nextInfoAgg, defaultInfoAgg, bilanciataKey, isEveryQuoteDisabled, showHeader } =
      useInfoAggiuntivaBilanciata(scommessaKey);

    const [selectedInfoAggKey, setSelectedInfoAggKey] = useState<string | undefined>();
    useEffect(() => {
      if (!isTruthy(nextInfoAgg?.length)) {
        setSelectedInfoAggKey(undefined);
        return;
      }

      if (!selectedInfoAggKey || !nextInfoAgg?.includes(selectedInfoAggKey)) {
        const nextSelection = defaultInfoAgg ?? nextInfoAgg?.[0];
        setSelectedInfoAggKey(nextSelection);
      }
    }, [nextInfoAgg, defaultInfoAgg, selectedInfoAggKey]);

    useEffect(() => {
      setSelectedInfoAggKey(defaultInfoAgg);
    }, [defaultInfoAgg]);

    const infoAggIndex = useInfoAggiuntivaIndex(selectedInfoAggKey);

    if (isEveryQuoteDisabled || !selectedInfoAggKey || !isTruthy(nextInfoAgg?.length)) {
      return <Skeleton {...props} />;
    }

    const { esitoKeyList } = infoAggIndex ?? {};
    return (
      <div className={classNames(styles.groupWrapper, { [styles.wrapperAvvenimento]: isAvvenimento })}>
        <ScommesseTableQuotaSelect
          key={`box-${selectedInfoAggKey}-${isAvvenimento}`}
          type={ScommesseLeafItem.InfoAggiuntivaWithEsito}
          size={sizeInfoAggiuntive?.[0]}
          disabled={showOnlyBilanciata}
          showHeader={showHeader}
          className={classNames(styles.select, { [styles.selectAvvenimento]: isAvvenimento })}
          containerClassName={classNames({ [styles.selectContainerAvvenimento]: isAvvenimento })}
          headerList={[]}
          optionsKeys={nextInfoAgg ?? []}
          scommessaKey={scommessaKey}
          bilanciataKey={bilanciataKey}
          infoAggiuntivaKey={selectedInfoAggKey!}
          selectedOptionKey={selectedInfoAggKey}
          onOptionSelected={setSelectedInfoAggKey}
        />
        {(esitoKeyList ?? Array(numeroEsiti).fill(null))?.map((keyEsito, i) => (
          <ScommesseEsitoTipoBox
            key={`box-${keyEsito}-${isAvvenimento}-${i}`}
            showHeader={isMobile || isEstesa || isAvvenimento || isAvvenimentoVirtual}
            keyEsito={keyEsito}
            scommessaKey={scommessaKey}
            infoAggiuntivaKey={selectedInfoAggKey!}
            gtagAction={gtagAction}
            className={isAvvenimento ? styles.containerAvvenimento : ''}
            classNameButton={isAvvenimento ? styles.boxAvvenimento : ''}
          />
        ))}
      </div>
    );
  },
  (prevParams, nextParam) =>
    prevParams.infoAggiuntivaKey === nextParam.infoAggiuntivaKey && prevParams.scommessaKey === nextParam.scommessaKey
);
